import { useState } from "react";
import { AlertTriangle, ShieldAlert, ArrowRight, FileEdit, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * PkiKonsistenzWarning · Blockierungs- / Warn-UI für die NBA→Höherstufungs-Brücke.
 *
 * Props:
 *   konsistenz · {
 *     status, level, message, requires_override,
 *     alternative_brief_typ?, begruendungsstaerke?, pflegegrad_tendenz?,
 *     bericht_typ, gewuenschter_pflegegrad
 *   }
 *   onAlternative   () => void   — User wählt "Leistungsanpassung"
 *   onProceedWithOverride (text) => void — User schreibt Begründung und fährt fort
 *   onCancel        () => void   — Schließt die Warnung
 *
 * Verhalten:
 *   level=block + status=no_gutachten      → Nur Hinweis + "OK" schließen
 *   level=block + status=eingeschraenkt    → "Stattdessen Leistungsanpassung" + "Abbrechen"
 *   level=warn  + status=warn_schwach      → Textarea + "Mit Begründung fortfahren" + "Stattdessen Leistungsanpassung"
 */
export default function PkiKonsistenzWarning({
  konsistenz,
  onAlternative,
  onProceedWithOverride,
  onCancel,
}) {
  const [overrideText, setOverrideText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!konsistenz) return null;

  const isBlock = konsistenz.level === "block";
  const isWarn = konsistenz.level === "warn";
  const noGutachten = konsistenz.status === "block_no_gutachten";

  const cls = isBlock
    ? "border-brand-darkred/45 bg-brand-darkred/8"
    : "border-saffron/55 bg-saffron/15";

  const iconColor = isBlock ? "text-brand-darkred" : "text-clay-dark";
  const Icon = isBlock ? ShieldAlert : AlertTriangle;

  const handleProceed = async () => {
    if (overrideText.trim().length < 25) return;
    setSubmitting(true);
    try {
      await onProceedWithOverride(overrideText.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="pki-konsistenz-warning"
      data-konsistenz-status={konsistenz.status}
      className={`rounded-2xl border-2 p-5 md:p-6 ${cls}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <Icon className={`h-6 w-6 ${iconColor} shrink-0 mt-0.5`} strokeWidth={2} />
        <div className="flex-1">
          <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-1.5">
            Konsistenz-Prüfung NBA ↔ {konsistenz.bericht_typ === "widerspruch" ? "Widerspruch" : "Höherstufung"}
          </div>
          <h3 className="font-display text-[1.2rem] text-sand-800 leading-tight font-semibold">
            {isBlock ? "Brief-Erstellung blockiert" : "Schwache NBA-Begründungslage"}
          </h3>
        </div>
        <button
          type="button"
          onClick={onCancel}
          data-testid="konsistenz-warning-close"
          className="text-sand-500 hover:text-sand-800"
          aria-label="Schließen"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <p className="text-[13.5px] text-sand-800 leading-relaxed mb-4">
        {konsistenz.message}
      </p>

      {/* Diagnose-Metadaten */}
      {(konsistenz.begruendungsstaerke || konsistenz.pflegegrad_tendenz) && (
        <div className="mb-4 rounded-xl border border-cream-300 bg-white p-3.5 space-y-1.5 text-[12.5px]">
          {konsistenz.begruendungsstaerke && (
            <div className="flex gap-2">
              <span className="text-sand-500 uppercase tracking-[0.18em] text-[10.5px] font-bold w-32 shrink-0">Begründungsstärke</span>
              <span className="text-sand-800 font-semibold">{konsistenz.begruendungsstaerke}</span>
            </div>
          )}
          {konsistenz.pflegegrad_tendenz && (
            <div className="flex gap-2">
              <span className="text-sand-500 uppercase tracking-[0.18em] text-[10.5px] font-bold w-32 shrink-0">NBA-Tendenz</span>
              <span className="text-sand-800">{konsistenz.pflegegrad_tendenz}</span>
            </div>
          )}
          {konsistenz.gewuenschter_pflegegrad != null && (
            <div className="flex gap-2">
              <span className="text-sand-500 uppercase tracking-[0.18em] text-[10.5px] font-bold w-32 shrink-0">Gewünschter PG</span>
              <span className="text-sand-800 font-semibold">PG {konsistenz.gewuenschter_pflegegrad}</span>
            </div>
          )}
        </div>
      )}

      {/* WARN-Modus: Textarea + Override */}
      {isWarn && (
        <div className="mb-4">
          <label
            htmlFor="konsistenz-override-text"
            className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-clay-dark mb-1.5"
          >
            Pflicht-Begründung (mind. 25 Zeichen) — wird im Audit-Log gespeichert
          </label>
          <textarea
            id="konsistenz-override-text"
            rows={4}
            value={overrideText}
            onChange={(e) => setOverrideText(e.target.value)}
            placeholder='z. B. "Trotz schwacher Aktendatenlage liegen zusätzliche Hinweise aus dem Hausbesuch vom … vor, die für Modul 4/5 eine deutlichere Beeinträchtigung belegen. Eine erneute Begutachtung wird gewünscht."'
            data-testid="konsistenz-override-text"
            className="w-full rounded-xl border border-cream-400 bg-cream-50 px-3 py-2.5 text-[13px] text-sand-800 focus:outline-none focus:border-terra"
          />
          <div className="mt-1 text-[11px] text-sand-500 text-right">
            {overrideText.trim().length} / 25 Zeichen
          </div>
        </div>
      )}

      {/* Action-Buttons */}
      <div className="flex flex-wrap items-center gap-2 justify-end">
        {noGutachten ? (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            data-testid="konsistenz-ok"
            className="h-10 rounded-full"
          >
            Verstanden
          </Button>
        ) : (
          <>
            {konsistenz.alternative_brief_typ && (
              <Button
                type="button"
                onClick={onAlternative}
                data-testid="konsistenz-action-alternative"
                className="h-10 rounded-full bg-sand-800 hover:bg-sand-700 text-white px-5 gap-1.5"
              >
                <FileEdit className="h-4 w-4" strokeWidth={2} />
                Stattdessen Antrag auf Leistungsanpassung
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            )}
            {isWarn && (
              <Button
                type="button"
                onClick={handleProceed}
                disabled={overrideText.trim().length < 25 || submitting}
                data-testid="konsistenz-action-proceed"
                className="h-10 rounded-full bg-brand-darkred hover:bg-brand-darkred/90 text-white px-5"
              >
                {submitting ? "Wird erstellt …" : "Mit Begründung fortfahren"}
              </Button>
            )}
            {isBlock && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                data-testid="konsistenz-cancel"
                className="h-10 rounded-full"
              >
                Abbrechen
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
